import {
	useMutation,
	useQueryClient,
	useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import {
	ArrowRightIcon,
	BuildingIcon,
	ShieldCheckIcon,
	UserMinusIcon,
	UserPlusIcon,
	UsersIcon,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { DataTable } from "@/components/data-table/data-table";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export const Route = createFileRoute("/admin/permissions")({
	component: AdminPermissions,
	ssr: false,
});

type OrganizationMembership = {
	id: string;
	organization: { id: string; name: string };
	user: {
		id: string;
		emailAddress: string;
		firstName?: string;
		lastName?: string;
	};
	role: string;
	createdAt: string;
};

type AdminOrganization = {
	id: string;
	name: string;
};

type AdminUser = {
	id: string;
	firstName: string | null;
	lastName: string | null;
	emailAddresses: Array<{ emailAddress: string }>;
	publicMetadata?: { admin?: boolean } | null;
	organizationMemberships: Array<{
		organization: { id: string; name: string };
	}>;
	createdAt: string;
	banned?: boolean;
};

function AdminPermissions() {
	const queryClient = useQueryClient();
	const [isAddMemberDialogOpen, setIsAddMemberDialogOpen] = useState(false);
	const [selectedOrgId, setSelectedOrgId] = useState<string>("");
	const [selectedUserId, setSelectedUserId] = useState<string>("");
	const [selectedRole, setSelectedRole] = useState<string>("member");
	const [membershipToRemove, setMembershipToRemove] = useState<string | null>(
		null,
	);

	const { data: organizations } = useSuspenseQuery<AdminOrganization[]>({
		queryKey: ["admin", "organizations"],
		queryFn: async () => {
			const response = await fetch("/api/admin/organizations");
			if (!response.ok) {
				throw new Error("Failed to fetch organizations");
			}
			const data = await response.json();
			return data.organizations as AdminOrganization[];
		},
	});

	const { data: users } = useSuspenseQuery<AdminUser[]>({
		queryKey: ["admin", "users"],
		queryFn: async () => {
			const response = await fetch("/api/admin/users");
			if (!response.ok) {
				throw new Error("Failed to fetch users");
			}
			const data = await response.json();
			return data.users as AdminUser[];
		},
	});

	const { data: memberships } = useSuspenseQuery({
		queryKey: ["admin", "memberships"],
		queryFn: async () => {
			const response = await fetch("/api/admin/memberships");
			if (!response.ok) {
				throw new Error("Failed to fetch memberships");
			}
			const data = await response.json();
			return data.memberships as OrganizationMembership[];
		},
	});

	const addMemberMutation = useMutation({
		mutationFn: async (data: {
			organizationId: string;
			userId: string;
			role: string;
		}) => {
			const response = await fetch("/api/admin/memberships", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(data),
			});
			if (!response.ok) {
				throw new Error("Failed to add member");
			}
			return response.json();
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["admin", "memberships"] });
			setIsAddMemberDialogOpen(false);
			setSelectedOrgId("");
			setSelectedUserId("");
			toast.success("Member added successfully");
		},
		onError: () => {
			toast.error("Failed to add member");
		},
	});

	const updateMembershipMutation = useMutation({
		mutationFn: async ({ id, role }: { id: string; role: string }) => {
			const response = await fetch(`/api/admin/memberships/${id}`, {
				method: "PATCH",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ role }),
			});
			if (!response.ok) {
				throw new Error("Failed to update membership");
			}
			return response.json();
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["admin", "memberships"] });
			toast.success("Membership updated successfully");
		},
		onError: () => {
			toast.error("Failed to update membership");
		},
	});

	const removeMemberMutation = useMutation({
		mutationFn: async (id: string) => {
			const response = await fetch(`/api/admin/memberships/${id}`, {
				method: "DELETE",
			});
			if (!response.ok) {
				throw new Error("Failed to remove member");
			}
			return response.json();
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["admin", "memberships"] });
			toast.success("Member removed successfully");
			setMembershipToRemove(null);
		},
		onError: () => {
			toast.error("Failed to remove member");
		},
	});

	const membershipColumns: ColumnDef<OrganizationMembership>[] = [
		{
			accessorKey: "user",
			header: "User",
			cell: ({ row }) => {
				const user = row.original.user;
				const name = [user.firstName, user.lastName].filter(Boolean).join(" ");
				return (
					<div>
						<div className="font-medium">{name || "No name"}</div>
						<div className="text-muted-foreground text-sm">
							{user.emailAddress}
						</div>
					</div>
				);
			},
		},
		{
			accessorKey: "organization",
			header: "Organization",
			cell: ({ row }) => (
				<div className="flex items-center gap-2">
					<BuildingIcon className="h-4 w-4 text-muted-foreground" />
					<span>{row.original.organization.name}</span>
				</div>
			),
		},
		{
			accessorKey: "role",
			header: "Role",
			cell: ({ row }) => (
				<Select
					defaultValue={row.original.role}
					onValueChange={(value) => {
						updateMembershipMutation.mutate({
							id: row.original.id,
							role: value,
						});
					}}
				>
					<SelectTrigger className="w-32">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="member">Member</SelectItem>
						<SelectItem value="admin">Admin</SelectItem>
					</SelectContent>
				</Select>
			),
		},
		{
			accessorKey: "createdAt",
			header: "Added",
			cell: ({ getValue }) =>
				new Date(getValue() as string).toLocaleDateString(),
		},
		{
			id: "actions",
			cell: ({ row }) => (
				<Button
					onClick={() => setMembershipToRemove(row.original.id)}
					size="sm"
					variant="ghost"
				>
					<UserMinusIcon className="h-4 w-4" />
				</Button>
			),
		},
	];

	return (
		<div className="space-y-6">
			<div>
				<h1 className="font-bold text-3xl">Permissions</h1>
				<p className="text-muted-foreground">
					Manage user permissions and organization memberships
				</p>
			</div>

			<Tabs className="space-y-4" defaultValue="users">
				<TabsList>
					<TabsTrigger value="users">Users</TabsTrigger>
					<TabsTrigger value="memberships">
						Organization Memberships
					</TabsTrigger>
					<TabsTrigger value="overview">Overview</TabsTrigger>
				</TabsList>

				<TabsContent className="space-y-4" value="users">
					<div className="flex items-center justify-between">
						<h2 className="font-semibold text-xl">All Users</h2>
					</div>

					<DataTable
						columns={[
							{
								accessorKey: "emailAddresses",
								header: "User",
								cell: ({ row }) => {
									const user = row.original;
									const name = [user.firstName, user.lastName]
										.filter(Boolean)
										.join(" ");
									return (
										<div>
											<div className="font-medium">{name || "No name"}</div>
											<div className="text-muted-foreground text-sm">
												{user.emailAddresses[0]?.emailAddress}
											</div>
										</div>
									);
								},
							},
							{
								accessorKey: "publicMetadata",
								header: "System Admin",
								cell: ({ row }) => (
									<span
										className={
											row.original.publicMetadata?.admin
												? "font-medium text-green-600"
												: "text-muted-foreground"
										}
									>
										{row.original.publicMetadata?.admin ? "Yes" : "No"}
									</span>
								),
							},
							{
								id: "organizations",
								header: "Organizations",
								cell: ({ row }) => {
									const userMemberships = memberships.filter(
										(m) => m.user.id === row.original.id,
									);
									return (
										<div className="flex flex-wrap gap-1">
											{userMemberships.length === 0 ? (
												<span className="text-muted-foreground text-sm">
													No organizations
												</span>
											) : (
												userMemberships.map((m) => (
													<span
														className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-1 text-xs"
														key={m.id}
													>
														{m.organization.name}
														{m.role === "admin" && (
															<ShieldCheckIcon className="h-3 w-3" />
														)}
													</span>
												))
											)}
										</div>
									);
								},
							},
							{
								accessorKey: "createdAt",
								header: "Created",
								cell: ({ getValue }) =>
									new Date(getValue() as string).toLocaleDateString(),
							},
						]}
						data={users}
					/>
				</TabsContent>

				<TabsContent className="space-y-4" value="memberships">
					<div className="flex items-center justify-between">
						<h2 className="font-semibold text-xl">Organization Memberships</h2>
						<Button onClick={() => setIsAddMemberDialogOpen(true)}>
							<UserPlusIcon className="mr-2 h-4 w-4" />
							Add Member
						</Button>
					</div>

					<DataTable columns={membershipColumns} data={memberships} />
				</TabsContent>

				<TabsContent className="space-y-4" value="overview">
					<div className="grid gap-4 md:grid-cols-2">
						<Card>
							<CardHeader>
								<CardTitle>Permission Model</CardTitle>
								<CardDescription>
									How permissions work in the system
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-4">
								<div className="flex items-start gap-3">
									<ShieldCheckIcon className="mt-0.5 h-5 w-5 text-muted-foreground" />
									<div>
										<h4 className="font-medium">Admin Users</h4>
										<p className="text-muted-foreground text-sm">
											Users with admin flag can access all organizations and
											manage the system
										</p>
									</div>
								</div>
								<div className="flex items-start gap-3">
									<BuildingIcon className="mt-0.5 h-5 w-5 text-muted-foreground" />
									<div>
										<h4 className="font-medium">Organization Members</h4>
										<p className="text-muted-foreground text-sm">
											Regular users can only access organizations they are
											members of
										</p>
									</div>
								</div>
								<div className="flex items-start gap-3">
									<UsersIcon className="mt-0.5 h-5 w-5 text-muted-foreground" />
									<div>
										<h4 className="font-medium">Organization Admins</h4>
										<p className="text-muted-foreground text-sm">
											Can manage members and settings within their organization
										</p>
									</div>
								</div>
							</CardContent>
						</Card>

						<Card>
							<CardHeader>
								<CardTitle>Quick Stats</CardTitle>
								<CardDescription>
									Current permission distribution
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-2">
								<div className="flex items-center justify-between">
									<span className="text-sm">Total Memberships</span>
									<span className="font-medium">{memberships.length}</span>
								</div>
								<div className="flex items-center justify-between">
									<span className="text-sm">Organization Admins</span>
									<span className="font-medium">
										{memberships.filter((m) => m.role === "admin").length}
									</span>
								</div>
								<div className="flex items-center justify-between">
									<span className="text-sm">System Admins</span>
									<span className="font-medium">
										{users.filter((user) => user.publicMetadata?.admin).length}
									</span>
								</div>
							</CardContent>
						</Card>
					</div>
				</TabsContent>
			</Tabs>

			<Dialog
				onOpenChange={setIsAddMemberDialogOpen}
				open={isAddMemberDialogOpen}
			>
				<DialogContent>
					<form
						onSubmit={(e) => {
							e.preventDefault();
							addMemberMutation.mutate({
								organizationId: selectedOrgId,
								userId: selectedUserId,
								role: selectedRole,
							});
						}}
					>
						<DialogHeader>
							<DialogTitle>Add Member to Organization</DialogTitle>
							<DialogDescription>
								Add a user to an organization with a specific role
							</DialogDescription>
						</DialogHeader>
						<div className="grid gap-4 py-4">
							<div className="grid gap-2">
								<Label>User</Label>
								<Select
									onValueChange={setSelectedUserId}
									value={selectedUserId}
								>
									<SelectTrigger>
										<SelectValue placeholder="Select a user" />
									</SelectTrigger>
									<SelectContent>
										{users.map((user) => (
											<SelectItem key={user.id} value={user.id}>
												{user.emailAddresses[0]?.emailAddress}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
							<div className="flex items-center justify-center">
								<ArrowRightIcon className="h-4 w-4 text-muted-foreground" />
							</div>
							<div className="grid gap-2">
								<Label>Organization</Label>
								<Select onValueChange={setSelectedOrgId} value={selectedOrgId}>
									<SelectTrigger>
										<SelectValue placeholder="Select an organization" />
									</SelectTrigger>
									<SelectContent>
										{organizations.map((org) => (
											<SelectItem key={org.id} value={org.id}>
												{org.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
							<div className="grid gap-2">
								<Label>Role</Label>
								<Select onValueChange={setSelectedRole} value={selectedRole}>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="member">Member</SelectItem>
										<SelectItem value="admin">Admin</SelectItem>
									</SelectContent>
								</Select>
							</div>
						</div>
						<DialogFooter>
							<Button
								onClick={() => {
									setIsAddMemberDialogOpen(false);
									setSelectedOrgId("");
									setSelectedUserId("");
								}}
								type="button"
								variant="outline"
							>
								Cancel
							</Button>
							<Button
								disabled={
									!(selectedOrgId && selectedUserId) ||
									addMemberMutation.isPending
								}
								type="submit"
							>
								{addMemberMutation.isPending ? "Adding..." : "Add Member"}
							</Button>
						</DialogFooter>
					</form>
				</DialogContent>
			</Dialog>

			<AlertDialog
				onOpenChange={(open) => {
					if (!open) {
						setMembershipToRemove(null);
					}
				}}
				open={Boolean(membershipToRemove)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Remove member</AlertDialogTitle>
						<AlertDialogDescription>
							This action will remove the member from the organization.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel onClick={() => setMembershipToRemove(null)}>
							Cancel
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => {
								if (membershipToRemove) {
									removeMemberMutation.mutate(membershipToRemove);
								}
								setMembershipToRemove(null);
							}}
						>
							Remove
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}
